import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Loads the user's most recent conversations for the history dropdown.
 * The history is a fixed window of the latest N conversations, not a
 * paginated archive, so there is deliberately no load-more here.
 */
const useRecentConversations = ({ chatService, isVisible, limit = 10 }) => {
  const [conversations, setConversations] = useState([]);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'error'
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Track if component is mounted
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  const loadConversations = useCallback(async () => {
    if (!chatService || status === 'loading') return;

    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setStatus('loading');
    setError(null);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await chatService.getAllConversations({ limit, offset: 0 });

      if (!isMountedRef.current) return;

      const items = Array.isArray(response) ? response : (response && response.conversations) || [];

      setConversations(items);
      setHasLoaded(true);
      setStatus('idle');
    } catch (err) {
      if (!isMountedRef.current || err.name === 'AbortError') return;

      setError('Failed to load conversations');
      setConversations([]);
      setHasLoaded(true);
      setStatus('error');
    } finally {
      if (isMountedRef.current) {
        abortControllerRef.current = null;
      }
    }
  }, [chatService, status, limit]);

  /**
   * Add a new conversation to the list
   */
  const addConversation = useCallback((conversation) => {
    setConversations((prev) => [conversation, ...prev]);
  }, []);

  /**
   * Update an existing conversation
   */
  const updateConversation = useCallback((conversationId, updates) => {
    setConversations((prev) => prev.map((conv) => (conv.id === conversationId ? { ...conv, ...updates } : conv)));
  }, []);

  /**
   * Load conversations when the dropdown first becomes visible
   */
  useEffect(() => {
    if (isVisible && !hasLoaded && status === 'idle') {
      loadConversations();
    }
  }, [isVisible, hasLoaded, status, loadConversations]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      // Abort any active requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // State
    conversations,
    isLoading: status === 'loading',
    error,

    // Methods
    addConversation,
    updateConversation,
  };
};

export default useRecentConversations;
